/**
 * Digital age-of-consent rules (COPPA / GDPR-K / DPDP).
 * Keep in sync with backend/parentalConsent.js
 */

export const COPPA_AGE = 13;
export const DEFAULT_CONSENT_AGE = 16;

export const DIGITAL_CONSENT_AGE_BY_COUNTRY: Record<string, number> = {
  'United States': 13,
  'United Kingdom': 13,
  Australia: 13,
  Canada: 13,
  Ireland: 13,
  Singapore: 13,
  'New Zealand': 13,
  'South Africa': 13,
  India: 18,
  France: 15,
  Spain: 14,
  Italy: 14,
  Austria: 14,
  Germany: 16,
  Netherlands: 16,
  Belgium: 13,
  Sweden: 13,
  Denmark: 13,
  Finland: 13,
  Norway: 13,
  Poland: 16,
  Portugal: 13,
  Greece: 15,
  Switzerland: 16,
  'United Arab Emirates': 18,
  'Saudi Arabia': 18,
  Qatar: 18,
  Kuwait: 18,
  Bahrain: 18,
  Oman: 18,
  China: 14,
  'Hong Kong': 18,
  Taiwan: 18,
  Japan: 15,
  'South Korea': 14,
  Malaysia: 18,
  Indonesia: 18,
  Thailand: 18,
  Philippines: 18,
  Vietnam: 16,
  Brazil: 18,
  Mexico: 18,
  Argentina: 13,
  Chile: 14,
  Colombia: 18,
  Peru: 18,
  Egypt: 18,
  Kenya: 18,
  Nigeria: 18,
  Other: DEFAULT_CONSENT_AGE,
};

export function normalizeCountry(country?: string | null): string {
  if (!country) return 'Other';
  const trimmed = country.trim();
  const match = Object.keys(DIGITAL_CONSENT_AGE_BY_COUNTRY).find(
    (c) => c.toLowerCase() === trimmed.toLowerCase(),
  );
  return match || 'Other';
}

export function getDigitalConsentAge(country?: string | null): number {
  const key = normalizeCountry(country);
  return DIGITAL_CONSENT_AGE_BY_COUNTRY[key] ?? DEFAULT_CONSENT_AGE;
}

export function calculateAge(dateOfBirth?: string | null, asOf: Date = new Date()): number | null {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  if (Number.isNaN(birth.getTime())) return null;
  let age = asOf.getFullYear() - birth.getFullYear();
  const m = asOf.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && asOf.getDate() < birth.getDate())) age -= 1;
  return age;
}

export interface RegistrationEligibility {
  age: number | null;
  consentAge: number;
  requiresParentRegistration: boolean;
  isBelow18: boolean;
  isBelowCoppaAge: boolean;
  reason: string | null;
  country: string;
}

export function evaluateRegistrationEligibility(
  dateOfBirth?: string | null,
  country?: string | null,
): RegistrationEligibility {
  const normalizedCountry = normalizeCountry(country);
  const consentAge = getDigitalConsentAge(normalizedCountry);
  const age = calculateAge(dateOfBirth);
  const isBelow18 = age != null && age < 18;
  const isBelowCoppaAge = age != null && age < COPPA_AGE;
  const requiresParentRegistration = age != null && age < consentAge;

  let reason: string | null = null;
  if (requiresParentRegistration) {
    if (isBelowCoppaAge) {
      reason = `COPPA: players under ${COPPA_AGE} must be registered by a parent or legal guardian.`;
    } else if (normalizedCountry === 'India') {
      reason = 'India DPDP: players under 18 must be registered by a parent or legal guardian.';
    } else {
      reason = `In ${normalizedCountry}, the digital age of consent is ${consentAge}. Players under ${consentAge} must be registered by a parent or legal guardian.`;
    }
  }

  return {
    age,
    consentAge,
    requiresParentRegistration,
    isBelow18,
    isBelowCoppaAge,
    reason,
    country: normalizedCountry,
  };
}

/** EncycGames 1.0: to add a child the adult must be 21+. A 21-year-old cannot have a child aged 6+. */
export const PARENT_MIN_AGE_TO_ADD_CHILD = 21;
export const MIN_PARENT_AGE_AT_BIRTH = 16;
export const ADD_CHILD_NEXT_KEY = 'encyc_add_child_next';

export interface ParentChildAgePolicy {
  country: string;
  parentMinAge: number;
  minAgeAtBirth: number;
  motherMaxAgeAtBirth: number;
  fatherMaxAgeAtBirth: number;
  motherWarnAgeAtBirth: number;
  fatherWarnAgeAtBirth: number;
}

export function getParentChildAgePolicy(country?: string | null): ParentChildAgePolicy {
  return {
    country: normalizeCountry(country),
    parentMinAge: PARENT_MIN_AGE_TO_ADD_CHILD,
    minAgeAtBirth: MIN_PARENT_AGE_AT_BIRTH,
    motherMaxAgeAtBirth: 50,
    fatherMaxAgeAtBirth: 70,
    motherWarnAgeAtBirth: 45,
    fatherWarnAgeAtBirth: 55,
  };
}

export type ParentChildRelationship = 'mother' | 'father' | 'guardian' | 'parent';

export interface ParentChildAgeCheck {
  level: 'ok' | 'yellow' | 'red';
  canSubmit: boolean;
  parentAge: number | null;
  childAge: number | null;
  ageAtBirth: number | null;
  policy: ParentChildAgePolicy;
  title: string | null;
  message: string | null;
  legalNotice: string;
}

/** Same domains as backend emailService verification skip. */
const TEST_ACCOUNT_EMAIL_DOMAINS = new Set([
  'test.com',
  'example.com',
  'example.org',
  'example.net',
  'mailinator.com',
  'users.encycgames.invalid',
  'encycgames.local',
]);

export function isTestAccountEmail(email?: string | null): boolean {
  const value = String(email || '').trim().toLowerCase();
  const at = value.lastIndexOf('@');
  if (at <= 0 || at === value.length - 1) return false;
  return TEST_ACCOUNT_EMAIL_DOMAINS.has(value.slice(at + 1));
}

export function allowsAgeGapSubmitForAccount(...emails: Array<string | null | undefined>): boolean {
  return emails.some((email) => isTestAccountEmail(email));
}

function resolveParentChildRoles(
  relationship?: string | null,
  parentGender?: string | null,
): { isMother: boolean; isFather: boolean; isGuardian: boolean } {
  const rel = String(relationship || '').trim().toLowerCase();
  const gender = String(parentGender || '').trim().toLowerCase();
  if (rel === 'mother') return { isMother: true, isFather: false, isGuardian: false };
  if (rel === 'father') return { isMother: false, isFather: true, isGuardian: false };
  if (rel === 'guardian') return { isMother: false, isFather: false, isGuardian: true };
  return {
    isMother: gender === 'female',
    isFather: gender === 'male',
    isGuardian: false,
  };
}

function relaxAgeGapBlockForTest(check: ParentChildAgeCheck, allowAgeGapSubmit?: boolean): ParentChildAgeCheck {
  if (!allowAgeGapSubmit || check.canSubmit) return check;
  if (check.parentAge == null) return check;
  if (check.parentAge < check.policy.parentMinAge) return check;
  return { ...check, canSubmit: true };
}

export function evaluateParentChildAges(input: {
  parentDateOfBirth?: string | null;
  childDateOfBirth?: string | null;
  parentGender?: string | null;
  relationship?: ParentChildRelationship | string | null;
  country?: string | null;
  allowAgeGapSubmit?: boolean;
  accountEmail?: string | null;
  accountEmails?: Array<string | null | undefined>;
} = {}): ParentChildAgeCheck {
  const {
    parentDateOfBirth,
    childDateOfBirth,
    parentGender,
    relationship,
    country,
    allowAgeGapSubmit,
    accountEmail,
    accountEmails,
  } = input;
  const allowTestSubmit =
    Boolean(allowAgeGapSubmit) ||
    allowsAgeGapSubmitForAccount(accountEmail, ...(accountEmails || []));
  const policy = getParentChildAgePolicy(country);
  const parentAge = calculateAge(parentDateOfBirth);
  const childAge = calculateAge(childDateOfBirth);
  const legalNotice =
    'If this is later verified and found to be false, it may be a criminal offence or carry legal consequences under the laws of your country. Either parent (mother or father) or a legal guardian may register a child. Typical father–child age gaps can be larger than mother–child gaps. Limits may vary by country.';

  const checked = (result: ParentChildAgeCheck) => relaxAgeGapBlockForTest(result, allowTestSubmit);

  if (parentAge == null) {
    return {
      level: 'red',
      canSubmit: false,
      parentAge,
      childAge,
      ageAtBirth: null,
      policy,
      title: 'Your date of birth is required',
      message: 'Add your date of birth on your player profile before adding a child.',
      legalNotice,
    };
  }
  if (parentAge < policy.parentMinAge) {
    return {
      level: 'red',
      canSubmit: false,
      parentAge,
      childAge,
      ageAtBirth: null,
      policy,
      title: `Parents / guardians must be ${policy.parentMinAge}+`,
      message: `You must be at least ${policy.parentMinAge} to add a child. You are ${parentAge}. A ${policy.parentMinAge}-year-old cannot have a child aged ${policy.parentMinAge - policy.minAgeAtBirth} or older.`,
      legalNotice,
    };
  }
  if (childAge == null) {
    return {
      level: 'ok',
      canSubmit: true,
      parentAge,
      childAge,
      ageAtBirth: null,
      policy,
      title: null,
      message: null,
      legalNotice,
    };
  }
  if (childAge >= parentAge) {
    return checked({
      level: 'red',
      canSubmit: false,
      parentAge,
      childAge,
      ageAtBirth: parentAge - childAge,
      policy,
      title: 'Child cannot be older than the parent',
      message: `Your age is ${parentAge} and the child’s age is ${childAge}.`,
      legalNotice,
    });
  }

  const ageAtBirth = parentAge - childAge;
  const { isMother, isFather, isGuardian } = resolveParentChildRoles(relationship, parentGender);
  const maxAtBirth = isMother ? policy.motherMaxAgeAtBirth : policy.fatherMaxAgeAtBirth;
  const warnAtBirth = isMother ? policy.motherWarnAgeAtBirth : policy.fatherWarnAgeAtBirth;

  if (!isGuardian && ageAtBirth < policy.minAgeAtBirth) {
    return checked({
      level: 'red',
      canSubmit: false,
      parentAge,
      childAge,
      ageAtBirth,
      policy,
      title: 'Parent and child ages conflict',
      message: `A ${parentAge}-year-old cannot typically have a ${childAge}-year-old child (you would have been about ${ageAtBirth} at the birth). Example: a 21-year-old cannot add a child aged 6 or older.`,
      legalNotice,
    });
  }
  if (!isGuardian && ageAtBirth > maxAtBirth) {
    return checked({
      level: 'red',
      canSubmit: false,
      parentAge,
      childAge,
      ageAtBirth,
      policy,
      title: 'Parent and child ages conflict',
      message: isMother
        ? `Mother–child gap looks biologically implausible (about ${ageAtBirth} years at birth; limit ${maxAtBirth} in ${policy.country}). Fathers may have a larger gap.`
        : `Parent–child gap looks implausible (about ${ageAtBirth} years at birth; limit ${maxAtBirth} in ${policy.country} for this parent type).`,
      legalNotice,
    });
  }
  if (isGuardian && (ageAtBirth < policy.minAgeAtBirth || ageAtBirth > policy.fatherWarnAgeAtBirth)) {
    return {
      level: 'yellow',
      canSubmit: true,
      parentAge,
      childAge,
      ageAtBirth,
      policy,
      title: 'Unusual guardian–child age gap',
      message: `You would have been about ${ageAtBirth} when this child was born. Confirm you are the legal guardian.`,
      legalNotice,
    };
  }
  if (!isGuardian && ageAtBirth >= warnAtBirth) {
    return {
      level: 'yellow',
      canSubmit: true,
      parentAge,
      childAge,
      ageAtBirth,
      policy,
      title: 'Unusual parent–child age gap',
      message: `You would have been about ${ageAtBirth} at the birth.${isFather ? ' Father–child gaps can be larger than mother–child gaps.' : ''}`,
      legalNotice,
    };
  }

  return {
    level: 'ok',
    canSubmit: true,
    parentAge,
    childAge,
    ageAtBirth,
    policy,
    title: null,
    message: null,
    legalNotice,
  };
}

export function rememberAddChildNext(): void {
  try {
    sessionStorage.setItem(ADD_CHILD_NEXT_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function consumeAddChildNext(): boolean {
  try {
    const pending = sessionStorage.getItem(ADD_CHILD_NEXT_KEY) === '1';
    if (pending) sessionStorage.removeItem(ADD_CHILD_NEXT_KEY);
    return pending;
  } catch {
    return false;
  }
}

export function peekAddChildNext(): boolean {
  try {
    return sessionStorage.getItem(ADD_CHILD_NEXT_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearAddChildNext(): void {
  try {
    sessionStorage.removeItem(ADD_CHILD_NEXT_KEY);
  } catch {
    /* ignore */
  }
}
